// libraries
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button, notification, Table, Tag, Tooltip, Typography } from 'antd';
import { blue, green, red } from '@ant-design/colors';

// components
import { SharedTable } from '../../shared/table';

// services
import * as WordTrackService from '../../../services/bb-api/word-track';

// utils
import { txt } from '../../../locales/i18n';
import { formatDate } from '../../../utils/date';

export type WordTrackTableProps = {
	content: Record<string, any>[];
	useLoading: [boolean, (state: boolean) => void];
	updateTableContent: () => Promise<void>;
};

export function WordTrackListContentDesktop(props: WordTrackTableProps): JSX.Element {
	const [loading, setLoading] = props.useLoading;
	const location = useLocation();

	function renderActions(wordTrack: Record<string, any>): JSX.Element {
		async function handleApprove(): Promise<void> {
			setLoading(true);

			try {
				await WordTrackService.approveMessage(wordTrack.id);
				notification.success({ message: txt('notification.update successful') });
				await props.updateTableContent();
			} catch (error) {
				notification.error({ message: txt('notification.update failed') });
			}

			setLoading(false);
		}

		async function handleUserApprove(): Promise<void> {
			setLoading(true);

			try {
				await WordTrackService.approveUserMessages(wordTrack.userId);
				notification.success({ message: txt('notification.update successful') });
				await props.updateTableContent();
			} catch (error) {
				notification.error({ message: txt('notification.update failed') });
			}

			setLoading(false);
		}

		return (
			<div className="item-action-container">
				<Button type="primary" className="approve-button" onClick={handleApprove}>
					{txt('global.approve')}
				</Button>

				<Button type="ghost" className="approve-all-button" onClick={handleUserApprove}>
					{txt('global.approve all')}
				</Button>
			</div>
		);
	}

	function renderMessage(wordTrack: Record<string, any>): JSX.Element {
		let route: string;

		if (location.hash === '#groups') {
			route = `/groups/${wordTrack.groupId}?message=${wordTrack.messageId}#messages`;
		} else {
			route = `/users/${wordTrack.userId}/conversations/${wordTrack.conversationId}?message=${wordTrack.messageId}&wt=${wordTrack.id}`;
		}

		return (
			<Tooltip title={txt('global.open conversation')}>
				<Typography.Link href={route} style={{ display: 'flex' }}>
					<Typography.Paragraph ellipsis={{ rows: 3 }}>
						<span dangerouslySetInnerHTML={{ __html: wordTrack.body }} />
					</Typography.Paragraph>
				</Typography.Link>
			</Tooltip>
		);
	}

	function renderFullMessage(body: string): JSX.Element {
		return (
			<div className="full-message-container">
				<Typography.Text>
					<span dangerouslySetInnerHTML={{ __html: body }} />
				</Typography.Text>
			</div>
		);
	}

	const expandable = {
		expandedRowRender: (record: Record<string, any>) => renderFullMessage(record.body),
	};

	function renderUser(wordTrack: Record<string, any>): JSX.Element {
		return (
			<Tooltip title={txt('global.open user')}>
				<Typography.Link href={`/users/${wordTrack.userId}`} className="user-container">
					<Typography.Text ellipsis>{wordTrack.username}</Typography.Text>
					<Typography.Text type="secondary">{wordTrack.municipality}</Typography.Text>
				</Typography.Link>
			</Tooltip>
		);
	}

	type Participant = {
		id: number;
		username: string;
		email: string;
		municipality: string;
	};

	function renderParticipants(wordTrack: { userId: number; participants: Participant[] }) {
		return wordTrack.participants?.map((participant) => {
			if (participant.id === wordTrack.userId) return null;

			return (
				<Tooltip key={participant.id} title={txt('global.open user')}>
					<Typography.Link href={`/users/${participant.id}`} className="user-container">
						<Typography.Text ellipsis>{participant.username}</Typography.Text>

						<Typography.Text type="secondary">
							{participant.municipality}
						</Typography.Text>
					</Typography.Link>
				</Tooltip>
			);
		});
	}

	function renderWords(wordTrack: Record<string, any>): JSX.Element {
		const words: string[] = Array.from(wordTrack.words);

		return (
			<Typography.Paragraph ellipsis={{ rows: 2 }}>
				{words.map((word: string, index: number) => (
					<Tag key={index} color="error">
						{word}
					</Tag>
				))}
			</Typography.Paragraph>
		);
	}

	return (
		<StyledTable
			expandable={expandable}
			rowKey="id"
			loading={loading}
			dataSource={props.content}
		>
			<Table.Column
				title={txt('global.approve')}
				width="18ch"
				align="center"
				render={renderActions}
			/>
			<Table.Column title={txt('global.user')} width="18ch" render={renderUser} />
			{location.hash === '' && (
				<Table.Column
					title={txt('global.participants')}
					render={renderParticipants}
					width="18ch"
				/>
			)}
			<Table.Column title={txt('global.text')} width="62ch" render={renderMessage} />
			<Table.Column title={txt('global.word')} width="28ch" render={renderWords} />
			<Table.Column
				dataIndex="created"
				title={txt('global.created')}
				width="18ch"
				render={formatDate}
			/>
		</StyledTable>
	);
}

const StyledTable = styled(SharedTable)`
	.user-cell-container {
		display: flex;
		gap: 1ch;
	}

	.item-action-container {
		display: flex;
		flex-direction: column;
		gap: 1ch;
	}

	.approve-button {
		background: ${green.primary};
		border-color: ${green.primary};
	}

	.approve-all-button {
		color: ${blue.primary};
		border-color: ${blue.primary};
	}

	span.word-highlight {
		color: ${red.primary};
		background-color: ${red[0]};
	}

	.user-container {
		display: flex;
		flex-direction: column;

		span:first-child {
			color: inherit;
		}
	}

	.full-message-container {
		max-width: 650px;
		margin: 1rem;
		white-space: pre-wrap;
	}
`;
